import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, type Grant, type Resident, type Transfer } from '../api.ts';

/**
 * Moving a resident's record between a home and a family — RX-ORG-06.
 *
 * The case the hybrid ownership model exists for, and the one neither a
 * pure-consumer nor a pure-institutional product can express: somebody is
 * admitted, and later goes home again. Four endpoints, no screen.
 *
 * A two-sided consent event, not an UPDATE. One side proposes and the other
 * approves, and the record of who did which outlives both — because "who was
 * responsible for this person's medication on that date" is a question asked
 * after something has gone wrong.
 */
export function PendingTransfers({ onChanged }: { onChanged: () => void }) {
  const [rows, setRows] = useState<Transfer[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ transfers: Transfer[] }>('/api/transfers');
      setRows(d.transfers);
    } catch {
      // A console that cannot list transfers must still list residents.
      setRows([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (rows.length === 0) return null;

  const act = async (t: Transfer, decision: 'approve' | 'reject') => {
    setBusy(t.id);
    try {
      await api.post(`/api/transfers/${t.id}/${decision}`);
      toast.success(
        decision === 'approve'
          ? `${t.patientName ?? 'The record'} moved to ${t.toName}`
          : 'Transfer declined',
      );
      await load();
      onChanged();
    } catch (e) {
      toast.error('Could not do that', e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
      <h3 className="font-medium">
        {rows.length === 1 ? 'A record is waiting to move' : `${rows.length} records are waiting to move`}
      </h3>
      <ul className="mt-3 space-y-3">
        {rows.map((t) => (
          <li key={t.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-(--line) bg-(--bg) p-3">
            <div className="min-w-0">
              <div className="font-medium">{t.patientName ?? 'A resident'}</div>
              <div className="text-sm text-(--muted)">
                {t.fromName} → {t.toName}
                {t.requestedByName && ` · asked for by ${t.requestedByName}`}
              </div>
              {t.reason && <div className="mt-1 text-sm">{t.reason}</div>}
            </div>

            <div className="ml-auto flex gap-2">
              {/*
                The requester cannot approve their own, and the server says
                so rather than the client guessing — a guess would either show
                a button that always fails or hide one that should work.
              */}
              {t.youRequestedIt ? (
                <span className="text-xs text-(--muted)">
                  Waiting for the other side
                </span>
              ) : (
                <button
                  disabled={busy === t.id}
                  onClick={() =>
                    setPending({
                      title: `Move ${t.patientName ?? 'this record'} to ${t.toName}?`,
                      body: `${t.toName} becomes responsible for this person's medication from the moment you approve. Everything recorded so far moves with them.`,
                      reassurance:
                        'Reminders keep firing throughout. Who did what, and when, stays in the record for both sides.',
                      confirmLabel: 'Approve the move',
                      onConfirm: () => act(t, 'approve'),
                    })
                  }
                  className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
                >
                  Approve
                </button>
              )}
              <button
                disabled={busy === t.id}
                onClick={() => void act(t, 'reject')}
                className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-red-500 disabled:opacity-40"
              >
                Decline
              </button>
            </div>
          </li>
        ))}
      </ul>

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </div>
  );
}

/**
 * Discharging a resident: handing the record back to a family member.
 *
 * The person taking it must already have access. That is not a convenience —
 * the transfer endpoint requires it, because a home that could name any user
 * id as the recipient could hand somebody's medical record to a stranger.
 * So this offers exactly the people who already hold a grant.
 */
export function DischargeDialog({ orgId, resident, onClose, onDone }: {
  /* Passed in rather than read off the resident: the roster response does
     not carry an owner, and inventing the field would mean trusting a value
     the API never sent. */
  orgId: string;
  resident: Resident;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const [grants, setGrants] = useState<Grant[] | null>(null);
  const [toUserId, setToUserId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ grants: Grant[] }>(`/api/patients/${resident.id}/grants`)
      .then((d) =>
        setGrants(
          d.grants.filter((g) => g.granteeType === 'user' && g.status === 'active'),
        ),
      )
      .catch(() => setGrants([]));
  }, [resident.id]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="discharge-title">
      <div className="w-full max-w-md rounded-2xl border border-(--line) bg-(--bg) p-5">
        <h2 id="discharge-title" className="text-lg font-semibold">Discharge {resident.displayName}</h2>
        <p className="mt-1 text-sm text-(--muted)">
          Hand the record back to a family member. They have to accept before
          anything moves, and the seat is freed when they do.
        </p>

        {grants === null && <p className="mt-4 text-sm text-(--muted)">Loading…</p>}

        {grants?.length === 0 && (
          /*
            A dead end with a way out, not just a refusal. The home cannot
            invent a recipient, and the fix is a specific thing somebody has
            to do first.
          */
          <p className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
            Nobody outside this home has access to {resident.displayName}&rsquo;s
            record, so there is nobody to hand it to. Invite the family member
            as a caregiver first — the record can only go to somebody who
            already holds it.
          </p>
        )}

        {grants && grants.length > 0 && (
          <label className="mt-4 block">
            <span className="text-xs text-(--muted)">Hand it to</span>
            <select
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            >
              <option value="">Choose somebody…</option>
              {grants.map((g) => (
                <option key={g.id} value={g.granteeId}>
                  {g.granteeName ?? g.invitePhone ?? 'Unnamed'} — {g.role}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="mt-3 block">
          <span className="text-xs text-(--muted)">Why — optional, and they will see it</span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Going home on the 5th"
            className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>

        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="min-h-11 rounded-lg border border-(--line) px-4 text-sm">
            Cancel
          </button>
          <button
            disabled={busy || !toUserId}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.post(`/api/orgs/${orgId}/transfers`, {
                  patientId: resident.id,
                  direction: 'out',
                  toUserId,
                  reason: reason.trim() || undefined,
                });
                toast.success(
                  'Discharge proposed',
                  'Nothing moves until they accept it.',
                );
                await onDone();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
                setBusy(false);
              }
            }}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Proposing…' : 'Propose the discharge'}
          </button>
        </div>
      </div>
    </div>
  );
}
